import type React from 'react';
import { useEffect, useRef } from 'react';

import { PageSkipButton, PromoteSlideUp } from '#src/app/pages/psf/share/shared/components';

import { usePageFooter } from './hooks/usePageFooter';

export const PageFooter: React.FC<EmptyProps> = () => {
	const {
		data: {
			skipLabel,
			skipUrl,
			isFooterTapHandledInPage,
			promoteSlideUp: { isEnabled, isOpen, isPending, amountLocalized },
		},
		actions: { handleSkipClick, handlePromoteSlideUpOpenChange, handlePromoteSlideUpChipIn, handlePromoteSlideUpSkip },
	} = usePageFooter();
	// The design system Button takes no ref, so the sheet restores focus to this wrapper instead.
	const skipControlRef = useRef<HTMLDivElement>(null);
	// Read through a ref so the listener is not torn down and re-attached twice on every tap.
	const isPendingRef = useRef(isPending);

	useEffect(() => {
		isPendingRef.current = isPending;
	}, [isPending]);

	// An effect only runs once hydrated, so a tap before that follows the href natively rather than
	// being cancelled by a listener with no JS behind it yet.
	useEffect(() => {
		const skipControl = skipControlRef.current;
		if (!skipControl || !isFooterTapHandledInPage) return undefined;

		const cancelNavigationAndHandleInPage = (event: MouseEvent) => {
			// The wrapper spans the footer's full width, so only a press on the link itself is a tap.
			if (!(event.target instanceof Element) || !event.target.closest('[data-qa="skip-link"]')) return;
			// Leave the browser's own open-in-new-tab and open-in-new-window gestures alone.
			if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

			event.preventDefault();
			// React attaches its listeners at the root, so stopping here keeps both the router's Link
			// handling and the anchor's own onClick from running the tap a second time.
			event.stopPropagation();

			// Cancelled before this bail on purpose: an impatient second tap during the wait must not
			// escape down the href and undo the tap already in flight.
			if (isPendingRef.current) return;

			handleSkipClick();
		};

		skipControl.addEventListener('click', cancelNavigationAndHandleInPage, true);

		return () => skipControl.removeEventListener('click', cancelNavigationAndHandleInPage, true);
	}, [isFooterTapHandledInPage, handleSkipClick]);

	return (
		<div className="px-4 py-7 text-center md:mt-5">
			<div ref={skipControlRef} data-qa="footer-skip-control">
				<PageSkipButton
					skipUrl={skipUrl}
					skipLabel={skipLabel}
					onSkipClick={isFooterTapHandledInPage ? undefined : handleSkipClick}
					loading={isPending}
				/>
			</div>
			{isEnabled && (
				<PromoteSlideUp
					isOpen={isOpen}
					amountLocalized={amountLocalized}
					onOpenChange={handlePromoteSlideUpOpenChange}
					onChipIn={handlePromoteSlideUpChipIn}
					onSkip={handlePromoteSlideUpSkip}
					restoreFocusRef={skipControlRef}
				/>
			)}
		</div>
	);
};
